/**
 * @file Source management page.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit3, Share2, Plus, Search, X, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader, Pagination } from '../../components/ui';
import { PAGINATION } from '../../constants/api';
import { cn } from '../../utils/cn';
import {
  SourcesService,
  Source,
  SourceFormData,
  isSourceActive,
} from '../../services/sourcesService';
import { isApiValidationError } from '../../utils/apiValidation';

const MODAL_TRANSITION_MS = 250;
const PER_PAGE = PAGINATION.DEFAULT_PER_PAGE;

/** Page wrapper: pointer cursor on all clickable controls */
const SOURCE_PAGE_CLASS =
  'source-management-page [&_button:not(:disabled)]:cursor-pointer [&_button:disabled]:cursor-not-allowed';

const SourceCreate: React.FC = () => {
  const [sources, setSources] = useState<Source[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PER_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef<number | null>(null);
  const initialLoadDoneRef = useRef(false);

  const [showModal, setShowModal] = useState(false);
  const [modalAnimatedIn, setModalAnimatedIn] = useState(false);
  const [editingSource, setEditingSource] = useState<Source | null>(null);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [modalError, setModalError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SourceFormData>({ name: '' });
  const [isSaving, setIsSaving] = useState(false);

  const showNotification = (message: string, type: 'success' | 'error' = 'error') => {
    setNotification({ message, type });
    window.setTimeout(() => setNotification(null), 3000);
  };

  const fetchSources = useCallback(async (page: number = 1) => {
    const isFirstLoad = !initialLoadDoneRef.current;
    setError(null);
    if (isFirstLoad) {
      setIsLoading(true);
    } else {
      setTableRefreshing(true);
    }

    try {
      const { sources: rows, pagination } = await SourcesService.listSources(page, PER_PAGE);
      setSources(rows);
      setCurrentPage(page);
      setTotalPages(pagination.total_page);
      setTotalRecords(pagination.total);
      setPageSize(pagination.per_page);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sources');
      setSources([]);
    } finally {
      setIsLoading(false);
      setTableRefreshing(false);
      initialLoadDoneRef.current = true;
    }
  }, []);

  const applySearch = useCallback(
    async (query: string) => {
      const q = query.trim();
      if (!q) {
        await fetchSources(1);
        return;
      }

      setTableRefreshing(true);
      setError(null);
      try {
        const { sources: all } = await SourcesService.listSources(1, 500);
        const lower = q.toLowerCase();
        const filtered = all.filter(
          (s) =>
            s.name.toLowerCase().includes(lower) ||
            s.id.includes(q) ||
            s.status.toLowerCase().includes(lower),
        );
        setSources(filtered);
        setCurrentPage(1);
        setTotalPages(1);
        setTotalRecords(filtered.length);
        setPageSize(Math.max(filtered.length, 1));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to search sources');
        setSources([]);
      } finally {
        setTableRefreshing(false);
      }
    },
    [fetchSources],
  );

  useEffect(() => {
    void fetchSources(1);
  }, [fetchSources]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showModal) {
      setModalAnimatedIn(false);
      return;
    }
    setModalAnimatedIn(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setModalAnimatedIn(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [showModal]);

  const resetForm = () => {
    setFormData({ name: '' });
    setFormErrors([]);
  };

  const handleOpenModal = (source: Source | null = null) => {
    if (source) {
      setEditingSource(source);
      setFormData({ name: source.name });
    } else {
      setEditingSource(null);
      resetForm();
    }
    setFormErrors([]);
    setModalError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setModalAnimatedIn(false);
    window.setTimeout(() => {
      setShowModal(false);
      setEditingSource(null);
      resetForm();
      setModalError(null);
    }, MODAL_TRANSITION_MS);
  };

  const handlePageChange = useCallback(
    (page: number) => {
      if (searchQuery.trim() || page < 1 || page > totalPages || page === currentPage) {
        return;
      }
      void fetchSources(page);
    },
    [fetchSources, totalPages, currentPage, searchQuery],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = window.setTimeout(() => {
      void applySearch(value);
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery('');
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    void applySearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = SourcesService.validateSourceForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setFormErrors([]);
    setIsSaving(true);

    try {
      if (editingSource) {
        await SourcesService.updateSource(editingSource.id, formData);
        showNotification('Source updated successfully', 'success');
      } else {
        await SourcesService.createSource(formData);
        showNotification('Source created successfully', 'success');
      }
      handleCloseModal();
      if (searchQuery.trim()) {
        await applySearch(searchQuery);
      } else {
        await fetchSources(currentPage);
      }
    } catch (err) {
      if (isApiValidationError(err)) {
        const messages = Object.values(err.errors).flatMap((v) =>
          Array.isArray(v) ? v : [String(v)],
        );
        setFormErrors(messages);
        setModalError(err.message || 'Please fix the validation errors below');
      } else {
        const message = err instanceof Error ? err.message : 'Unknown error occurred';
        setModalError(message);
        showNotification(message, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusSwitch = async (source: Source) => {
    const nextStatus = isSourceActive(source.status) ? 'inactive' : 'active';
    try {
      await SourcesService.toggleSourceStatus(source.id, nextStatus);
      showNotification('Source status updated successfully', 'success');
      if (searchQuery.trim()) {
        await applySearch(searchQuery);
      } else {
        await fetchSources(currentPage);
      }
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to update status', 'error');
    }
  };

  const deleteSource = async (source: Source) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete source "${source.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await SourcesService.deleteSource(source.id);
      if (searchQuery.trim()) {
        await applySearch(searchQuery);
      } else {
        await fetchSources(currentPage);
      }
      await Swal.fire({
        title: 'Deleted!',
        text: 'Source has been deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete source';
      await Swal.fire({
        title: 'Error',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  if (isLoading) {
    return (
      <div className={cn('space-y-6', SOURCE_PAGE_CLASS)}>
        <PageHeader title="Source Management" subtitle="Manage lead sources" subtitleClassName="text-blue-100 text-sm" />
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-2 text-gray-600">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            <span className="text-lg font-medium">Loading sources...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('space-y-6', SOURCE_PAGE_CLASS)}>
        <PageHeader title="Source Management" subtitle="Manage lead sources" subtitleClassName="text-blue-100 text-sm" />
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => {
              initialLoadDoneRef.current = false;
              setIsLoading(true);
              void fetchSources(1);
            }}
            className="cursor-pointer px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', SOURCE_PAGE_CLASS)}>
      {notification && (
        <div
          className={`p-4 rounded-md text-white ${notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}
        >
          {notification.message}
        </div>
      )}

      <PageHeader
        title="Source Management"
        subtitle="View and update lead sources"
        icon={Share2}
        subtitleClassName="text-blue-100 text-sm"
        actions={
          <button
            type="button"
            onClick={() => handleOpenModal()}
            className="cursor-pointer px-6 py-3 bg-white text-gray-900 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors flex items-center"
          >
            <Plus size={20} className="mr-2" /> New Source
          </button>
        }
      />

      <div className="relative bg-white rounded-lg overflow-hidden border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <h3 className="shrink-0 text-lg font-semibold text-gray-900">All Sources</h3>
          <div className="relative w-full sm:max-w-xs sm:min-w-[16rem]">
            <Search
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name or ID…"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              aria-label="Search sources"
            />
            {searchQuery.length > 0 && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto bg-white">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Source Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sources.map((source) => (
                <tr key={String(source.id)} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{source.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{source.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isSourceActive(source.status)}
                      aria-label={isSourceActive(source.status) ? 'Deactivate source' : 'Activate source'}
                      onClick={() => void handleStatusSwitch(source)}
                      className={cn(
                        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                        isSourceActive(source.status) ? 'bg-green-500' : 'bg-gray-300',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition',
                          isSourceActive(source.status) ? 'translate-x-5' : 'translate-x-0',
                        )}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleOpenModal(source)}
                        className="cursor-pointer rounded-md p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800"
                        title="Update Source"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteSource(source)}
                        className="cursor-pointer rounded-md p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-800"
                        title="Delete Source"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sources.length === 0 && (
            <div className="text-center py-12">
              <Share2 size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {searchQuery.trim() ? 'No sources match your search.' : 'No sources found.'}
              </p>
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={totalRecords}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          itemLabel="sources"
          disabled={tableRefreshing}
          ariaLabel="Sources pagination"
        />
      </div>

      {showModal && (
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center p-4',
            'bg-white/50 backdrop-blur-md transition-opacity duration-250 ease-out',
            modalAnimatedIn ? 'opacity-100' : 'opacity-0',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="source-modal-title"
        >
          <div
            className={cn(
              'w-full max-w-md rounded-xl bg-white p-8 shadow-2xl transition-all duration-250 ease-out',
              modalAnimatedIn ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-2',
            )}
          >
            <h2 id="source-modal-title" className="text-xl font-bold text-gray-900 mb-4">
              {editingSource ? 'Update Source' : 'Create New Source'}
            </h2>

            {modalError && (
              <div
                className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {modalError}
              </div>
            )}

            {formErrors.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <ul className="text-sm text-red-600 space-y-1">
                  {formErrors.map((err, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-red-500 mr-2">•</span>
                      <span>{err}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Source Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ name: e.target.value });
                    if (formErrors.length > 0) setFormErrors([]);
                  }}
                  className={cn(
                    'w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    formErrors.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-300',
                  )}
                  placeholder="Enter source name"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="cursor-pointer rounded-md bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !formData.name.trim() || formErrors.length > 0}
                  className={cn(
                    'rounded-md px-5 py-2.5 text-sm font-medium transition-all',
                    isSaving || !formData.name.trim() || formErrors.length > 0
                      ? 'cursor-not-allowed bg-slate-200 text-slate-400'
                      : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                  )}
                >
                  {isSaving
                    ? 'Saving...'
                    : editingSource
                      ? 'Update Source'
                      : 'Create Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceCreate;
